import { Box, Typography, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip, TablePagination, TextField, FormControl, InputLabel, Select, MenuItem, Avatar } from '@mui/material';
import { Edit, Delete, Visibility, CheckCircle } from '@mui/icons-material';
import { Clergy } from '../../types';

interface ClergyTableProps {
  clergy: Clergy[];
  loading: boolean;
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onEdit: (clergy: Clergy) => void;
  onDelete: (id: number) => void;
  onToggleActive: (id: number) => void;
}

const getRankLabel = (rank?: string) => {
  if (!rank) return '-';
  const labels: Record<string, string> = {
    BISHOP: 'Епископ',
    PRIEST: 'Священник',
    DEACON: 'Диакон',
    MONK: 'Монах',
  };
  return labels[rank] || rank;
};

export function ClergyTable({ clergy, loading, total, page, limit, onPageChange, onEdit, onDelete, onToggleActive }: ClergyTableProps) {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Фото</TableCell>
            <TableCell>Имя</TableCell>
            <TableCell>Сан</TableCell>
            <TableCell>Должность</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Действия</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {clergy.map((person) => (
            <TableRow key={person.id} hover>
              <TableCell>
                <Avatar 
                  src={person.photoUrl} 
                  alt={person.name}
                  sx={{ width: 40, height: 40 }}
                />
              </TableCell>
              <TableCell>{person.name}</TableCell>
              <TableCell>{getRankLabel(person.rank)}</TableCell>
              <TableCell>{person.position || '-'}</TableCell>
              <TableCell>
                <Chip 
                  label={person.isActive ? 'Активен' : 'Неактивен'} 
                  size="small" 
                  color={person.isActive ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Просмотр">
                    <IconButton size="small" onClick={() => onEdit(person)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Редактировать">
                    <IconButton size="small" onClick={() => onEdit(person)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={person.isActive ? 'Деактивировать' : 'Активировать'}>
                    <IconButton size="small" onClick={() => onToggleActive(person.id)}>
                      <CheckCircle fontSize="small" color={person.isActive ? 'success' : 'disabled'} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Удалить">
                    <IconButton size="small" onClick={() => onDelete(person.id)} sx={{ color: 'error.main' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {clergy.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="text.secondary">Данные не найдены</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={limit}
        onRowsPerPageChange={() => {}}
        rowsPerPageOptions={[]}
      />
    </TableContainer>
  );
}

interface ClergyToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  rankFilter: string;
  onRankFilterChange: (value: string) => void;
}

export function ClergyToolbar({ search, onSearchChange, rankFilter, onRankFilterChange }: ClergyToolbarProps) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
      <TextField
        label="Поиск"
        variant="outlined"
        size="small"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{ minWidth: 200 }}
      />
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Сан</InputLabel>
        <Select
          value={rankFilter}
          label="Сан"
          onChange={(e) => onRankFilterChange(e.target.value as string)}
        >
          <MenuItem value="">Все</MenuItem>
          <MenuItem value="BISHOP">Епископ</MenuItem>
          <MenuItem value="PRIEST">Священник</MenuItem>
          <MenuItem value="DEACON">Диакон</MenuItem>
          <MenuItem value="MONK">Монах</MenuItem>
        </Select>
      </FormControl>
      {(search || rankFilter) && (
        <Box sx={{ borderLeft: '1px solid #ccc', ml: 1, pl: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Активны фильтры
          </Typography>
        </Box>
      )}
    </Box>
  );
}
